import { ref } from 'vue';
import type { Router } from 'vue-router';
import {
  PROJECT_WBS_TAB_ID,
  projectDocumentTabId,
  type ProjectDynamicTab,
} from '@/utils/projectDetailTabState';
import { projectDashboardTabKey } from '@/utils/projectPageTab';
import { useAppShellStore } from '@/stores/useAppShellStore';

/** 当前项目内激活的 Tab id（供 AI 面板等非 Dashboard 子树读取） */
export const projectActiveInnerTabId = ref('info');

export function setProjectActiveInnerTabId(tabId: string) {
  projectActiveInnerTabId.value = tabId || 'info';
}

export function isProjectDocumentInnerTabActive(): boolean {
  return projectActiveInnerTabId.value.startsWith('doc:');
}

export type ProjectInnerTabOpenRequest =
  | {
      kind: 'wbs';
      projectId: number;
      /** 打开后是否自动触发生成 */
      generate?: boolean;
      label?: string;
    }
  | {
      kind: 'document';
      projectId: number;
      objectKey: string;
      fileName?: string;
      label?: string;
    };

type Listener = (req: ProjectInnerTabOpenRequest) => boolean | void;

const listeners = new Set<Listener>();
let pending: ProjectInnerTabOpenRequest | null = null;

export function subscribeProjectInnerTabOpen(fn: Listener): () => void {
  listeners.add(fn);
  if (pending) {
    const req = pending;
    pending = null;
    try {
      if (fn(req) !== true) pending = req;
    } catch {
      pending = req;
    }
  }
  return () => {
    listeners.delete(fn);
  };
}

export function consumePendingProjectInnerTabOpen(): ProjectInnerTabOpenRequest | null {
  const req = pending;
  pending = null;
  return req;
}

/** 构建跳转到项目详情并打开内部 Tab 的 query */
export function projectInnerTabQuery(req: ProjectInnerTabOpenRequest): Record<string, string> {
  if (req.kind === 'wbs') {
    const q: Record<string, string> = { innerTab: PROJECT_WBS_TAB_ID };
    if (req.generate) q.generate = '1';
    return q;
  }
  const q: Record<string, string> = {
    innerTab: 'doc',
    objectKey: req.objectKey,
  };
  if (req.fileName) q.fileName = req.fileName;
  return q;
}

/** 通知已挂载的 ProjectDashboard；若无监听则暂存，并可选跳转到项目详情 */
export function requestOpenProjectInnerTab(
  req: ProjectInnerTabOpenRequest,
  options?: { router?: Router; navigateIfNeeded?: boolean },
) {
  let handled = false;
  for (const fn of listeners) {
    try {
      if (fn(req) === true) handled = true;
    } catch (e) {
      console.error(e);
    }
  }
  if (!handled) pending = req;

  const router = options?.router;
  const navigateIfNeeded = options?.navigateIfNeeded !== false;
  if (!router || !navigateIfNeeded) return;

  const shell = useAppShellStore();
  const dashKey = projectDashboardTabKey(req.projectId);
  const onDashboard =
    shell.activePageTabKey === dashKey
    || (router.currentRoute.value.name === 'ProjectDashboard'
      && String(router.currentRoute.value.params.id ?? '') === String(req.projectId));

  if (onDashboard && handled) return;

  const query = projectInnerTabQuery(req);
  void router.push({
    name: 'ProjectDashboard',
    params: { id: String(req.projectId) },
    query,
  });
}

export function buildWbsDynamicTab(label?: string): ProjectDynamicTab {
  return { kind: 'wbs', id: 'wbs', label: (label ?? '').trim() || 'WBS' };
}

export function buildDocumentDynamicTab(args: {
  objectKey: string;
  fileName?: string;
  label?: string;
}): ProjectDynamicTab {
  const objectKey = args.objectKey.trim();
  const fileName = (args.fileName ?? '').trim();
  const label = (args.label ?? '').trim() || fileName || '文档查看';
  return {
    kind: 'document',
    id: projectDocumentTabId(objectKey),
    objectKey,
    fileName,
    label,
  };
}
